import { apiRequest } from "../client";
import type { PlanFeaturesSnapshot } from "@/calculators/planFeatures/types";

const base = (faClientId: string) =>
  `/agentic-v2/fa-clients/${faClientId}/calculators/plan-features`;

export async function getPlanFeaturesSnapshot(
  faClientId: string,
): Promise<PlanFeaturesSnapshot | null> {
  const res = await apiRequest<{ snapshot: PlanFeaturesSnapshot | null }>({
    url: base(faClientId),
    method: "GET",
  });
  return res.snapshot ?? null;
}

export type PlanFeaturesCompileTaskStatus = {
  taskId: string;
  status: string;
  error?: string | null;
  snapshot?: PlanFeaturesSnapshot | null;
};

export async function startPlanFeaturesCompileAsync(
  faClientId: string,
): Promise<{ taskId: string; status: string }> {
  return apiRequest<{ taskId: string; status: string }>({
    url: `${base(faClientId)}/compile/async`,
    method: "POST",
  });
}

export async function getPlanFeaturesCompileTask(
  faClientId: string,
  taskId: string,
): Promise<PlanFeaturesCompileTaskStatus> {
  return apiRequest<PlanFeaturesCompileTaskStatus>({
    url: `${base(faClientId)}/compile/tasks/${taskId}`,
    method: "GET",
  });
}

const POLL_MS = 2000;
const MAX_POLL_MS = 30 * 60 * 1000;

export async function compilePlanFeaturesSnapshot(
  faClientId: string,
  options?: { shouldContinue?: () => boolean },
): Promise<PlanFeaturesSnapshot> {
  const keepPolling = () => options?.shouldContinue?.() !== false;
  const { taskId } = await startPlanFeaturesCompileAsync(faClientId);
  const deadline = Date.now() + MAX_POLL_MS;
  while (Date.now() < deadline) {
    if (!keepPolling()) {
      throw new Error("Compile cancelled");
    }
    await new Promise((r) => setTimeout(r, POLL_MS));
    if (!keepPolling()) {
      throw new Error("Compile cancelled");
    }
    const task = await getPlanFeaturesCompileTask(faClientId, taskId);
    if (task.status === "failed" || task.status === "cancelled") {
      throw new Error(task.error || "Plan features compile failed");
    }
    if (task.status === "completed") {
      if (!task.snapshot) {
        throw new Error("Compile finished without a snapshot");
      }
      return task.snapshot;
    }
  }
  throw new Error("Plan features compile is still running — try refreshing in a minute.");
}

export async function savePlanFeaturesSnapshot(
  faClientId: string,
  snapshot: PlanFeaturesSnapshot,
): Promise<PlanFeaturesSnapshot> {
  const res = await apiRequest<{ snapshot: PlanFeaturesSnapshot }>({
    url: base(faClientId),
    method: "PUT",
    data: snapshot,
  });
  if (!res.snapshot) {
    throw new Error("Save returned no snapshot");
  }
  return res.snapshot;
}
